import { useEffect, useState, useMemo } from "react";
import {
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Link,
  Box,
  Menu,
  InputLabel,
} from "@mui/material";
import { useLanguage } from "../../context/LanguageContext";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import api from "../../api/api";
import DepositConfirmModal from "../Payment/DepositConfirmModal";
import { stringService } from "../../services/string.service";

import "../../assets/style/Payments.css";

import BtcIcon from "../../assets/img/icons/btc.png";
import EtheriumIcon from "../../assets/img/icons/eth.png";
import TetherUSDTIcon from "../../assets/img/icons/usdt.png";
import BNBIcon from "../../assets/img/icons/bnb.png";
import SolanaIcon from "../../assets/img/icons/sol.png";
import XRPIcon from "../../assets/img/icons/xrp.png";
import CardanoIcon from "../../assets/img/icons/ada.png";
import DogecoinIcon from "../../assets/img/icons/doge.png";
import LitecoinIcon from "../../assets/img/icons/ltc.png";
import MoneroIcon from "../../assets/img/icons/xmr.png";
import PolygonIcon from "../../assets/img/icons/matic.png";
import TRONIcon from "../../assets/img/icons/trx.png";
import USDIcon from "../../assets/img/icons/usd.png";
import EURIcon from "../../assets/img/icons/eur.png";

const CurrencyIcon = ({ img }) => (
  <div className="currency-icon-placeholder">
    <img src={img} alt="" />
  </div>
);

const currencies = [
  { symbol: "BTC", name: "Bitcoin", icon: <CurrencyIcon img={BtcIcon} /> },
  {
    symbol: "ETH",
    name: "Ethereum",
    icon: <CurrencyIcon img={EtheriumIcon} />,
  },
  {
    symbol: "USDT",
    name: "Tether USDT",
    icon: <CurrencyIcon img={TetherUSDTIcon} />,
  },
  { symbol: "BNB", name: "BNB", icon: <CurrencyIcon img={BNBIcon} /> },
  { symbol: "Sol", name: "Solana", icon: <CurrencyIcon img={SolanaIcon} /> },
  { symbol: "XRP", name: "XRP", icon: <CurrencyIcon img={XRPIcon} /> },
  {
    symbol: "Cardano",
    name: "Cardano",
    icon: <CurrencyIcon img={CardanoIcon} />,
  },
  {
    symbol: "Doge",
    name: "Dogecoin",
    icon: <CurrencyIcon img={DogecoinIcon} />,
  },
  {
    symbol: "LTC",
    name: "Litecoin",
    icon: <CurrencyIcon img={LitecoinIcon} />,
  },
  { symbol: "Monero", name: "Monero", icon: <CurrencyIcon img={MoneroIcon} /> },
  {
    symbol: "Polygon",
    name: "Polygon",
    icon: <CurrencyIcon img={PolygonIcon} />,
  },
  { symbol: "TRON", name: "TRON", icon: <CurrencyIcon img={TRONIcon} /> },
  { symbol: "USD", name: "USD", icon: <CurrencyIcon img={USDIcon} /> },
  { symbol: "EUR", name: "EUR", icon: <CurrencyIcon img={EURIcon} /> },
];

function WithdrawDepositForm() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Withdrawal, 1 = Deposit
  const { t } = useLanguage();

  const [amount, setAmount] = useState("0.00");
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState("BTC");
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [selectedMethodId, setSelectedMethodId] = useState(""); // 1. Состояние для хранения ID выбранного метода
  const [paymentsData, setPaymentsData] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  
  // Состояния для валидации баланса
  const [userBalance, setUserBalance] = useState(0);
  const [balanceError, setBalanceError] = useState("");
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const handleSelectChange = (event) => {
    const newId = event.target.value;
    setSelectedMethodId(newId);

    // 👇 Находим объект и сразу устанавливаем его имя в отдельное состояние
    const selectedMethod = paymentsData.find(method => method.id === newId);    
    if (selectedMethod) {
      const name = selectedMethod.bank_name || `VISA *${stringService.getLast4(selectedMethod.card_number)}`;
      setSelectedPaymentMethod(name);
    }
  }


  const [assetsToken, setAssetsToken] = useState(
    sessionStorage.getItem("accessToken")
  );

  // Функция для загрузки баланса пользователя
  const loadUserBalance = async () => {
    setIsLoadingBalance(true);
    try {
      const response = await api.get("/auth/user", {
        headers: { Authorization: `Bearer ${assetsToken}` },
      });
      
      // Предполагаем, что баланс хранится в поле balance или wallet_balance
      const balance = response.data.balance || response.data.wallet_balance || 0;
      setUserBalance(balance);
      setBalanceError("");
    } catch (err) {
      console.error("Ошибка загрузки баланса:", err);
      setBalanceError("Failed to load balance");
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Сбрасываем ошибки при смене вкладки
    setBalanceError("");
    // Сбрасываем валидацию для новой вкладки
    if (newValue === 0) { // Withdrawal
      const numAmount = parseFloat(amount) || 0;
      if (numAmount > userBalance) {
        setBalanceError("Insufficient balance");
      }
    }
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleCurrencySelect = (symbol) => {
    setSelectedCurrencySymbol(symbol);
    handleMenuClose();
    
    // Перезагружаем баланс для новой валюты
    loadUserBalance();
    
    // Проверяем баланс для текущей суммы
    if (activeTab === 0) {
      const numAmount = parseFloat(amount) || 0;
      if (numAmount > userBalance) {
        setBalanceError("Insufficient balance");
      } else {
        setBalanceError("");
      }
    }
  };

  const selectedCurrency = currencies.find(
    (c) => c.symbol === selectedCurrencySymbol
  );

  useEffect(() => {
    const getPaymentMethods = async () => {
      try {
        const res = await api.get("/payment-methods", {
          headers: { Authorization: `Bearer ${assetsToken}` },
        });
        const paymentsDataArr = [...res.data.accounts, ...res.data.cards];
        setPaymentsData(paymentsDataArr);
      } catch (err) {
        console.log("Ошибка получения методов оплаты");
      }
    };
    getPaymentMethods();
  }, [assetsToken]);

  // Загружаем баланс при монтировании компонента
  useEffect(() => {
    loadUserBalance();
  }, []);

  const handleAmountChange = (event) => {
    const value = event.target.value;
    // разрешает цифры и одну точку
    const regex = /^[0-9]*\.?[0-9]*$/;

    if (regex.test(value)) {
      console.log(regex.test(value));
      setAmount(value);
      
      // Валидация баланса для withdrawal
      if (activeTab === 0) {
        const numAmount = parseFloat(value) || 0;
        if (numAmount > userBalance) {
          setBalanceError("Insufficient balance");
        } else {
          setBalanceError("");
        }
      }
    }
  };

  const isButtonDisabled = useMemo(() => {
    const isDepositTab = activeTab === 1;
    const isWithdrawalTab = activeTab === 0;
    const isAmountEntered = parseFloat(amount) > 0;
    const isMethodSelected = selectedMethodId !== "";
    
    // Для withdrawal проверяем баланс
    if (isWithdrawalTab) {
      const numAmount = parseFloat(amount) || 0;
      const hasInsufficientBalance = numAmount > userBalance;
      return !(isAmountEntered && isMethodSelected && !hasInsufficientBalance);
    }
    
    // Для deposit
    return !(isDepositTab && isAmountEntered && isMethodSelected);
  }, [amount, selectedMethodId, activeTab, userBalance]);

  const handleOpenConfirmModal = () => {
    // Проверяем баланс перед открытием модального окна
    if (activeTab === 0) {
      const numAmount = parseFloat(amount) || 0;
      if (numAmount > userBalance) {
        setBalanceError("Insufficient balance");
        return; // Не открываем модальное окно
      }
    }
    
    setIsConfirmModalOpen(true);
  };
  
  const handleConfirmDeposit = () => {
    //  транзакция api.post(...)
    console.log("Отправка транзакции на сервер...");
    
    setIsConfirmModalOpen(false); // Закрываем окно после подтверждения
    
    // Обновляем баланс после успешной транзакции
    setTimeout(() => {
      loadUserBalance();
    }, 1000);
  };



  return (
    <>
      <Paper elevation={0} className="card payment-form-card">
        {/* Вкладки */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className="payment-tabs"
          variant="fullWidth" // Растягивает вкладки на всю ширину
        >
          <Tab label={t('withdrawal')} className="payment-tab" />
          <Tab label={t('deposit')} className="payment-tab" />
        </Tabs>

        {/* Контент формы */}
        <div className="form-content">
          <div className="form-row-space-between">
            <Typography variant="body2" className="form-label">
              {t('paymentMethod')}
            </Typography>
            <Link href="#" className="edit-link">
              {t('edit')}
            </Link>
          </div>

          <FormControl fullWidth variant="outlined" className="custom-select">
            <InputLabel>{t('paymentMethod')}</InputLabel>
            <Select
              value={selectedMethodId}
              onChange={handleSelectChange}
              label={t('paymentMethod')}
            >
              {/* 4. Map генерирует MenuItem'ы ВНУТРИ Select */}
              {paymentsData.map((payMethod) => (
                
                <MenuItem key={payMethod.id} value={payMethod.id} >
                  {payMethod.bank_name || payMethod.card_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="form-row-space-between">
            <Typography variant="body2" className="form-label">
              {t('amount')}
            </Typography>
            <Typography variant="caption" className="balance-hint">
              {isLoadingBalance ? t('loadingBalance') : 
               balanceError === "Failed to load balance" ? t('errorLoadingBalance') :
               `${t('balance')}: ${userBalance.toFixed(8)} ${selectedCurrencySymbol}`}
            </Typography>
          </div>

          <TextField
            fullWidth
            variant="outlined"
            className="custom-textfield amount-field"
            placeholder="0.00"
            value={amount}
            onChange={handleAmountChange}
            InputProps={{
              endAdornment: (
                // иконка валюты
                <Button className="currency-selector" onClick={handleMenuOpen}>
                  {selectedCurrency.icon}
                  <Typography>{selectedCurrency.name}</Typography>
                  <div className="currency-selector-arrow">▼</div>
                </Button>
              ),
            }}
          />

          {/* Меню, привязанное к состоянию anchorEl */}
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            classes={{ paper: "currency-menu-paper" }}
          >
            {currencies.map((currency) => (
              <MenuItem
                key={currency.symbol}
                onClick={() => handleCurrencySelect(currency.symbol)}
                className="currency-menu-item"
              >
                {currency.icon}
                <Typography>{currency.name}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <div className="info-row">
            <Typography className="info-label">{t('fee')}</Typography>
            <Typography className="info-value">BTC 0 (0,00 $)</Typography>
          </div>
          <div className="info-row">
            <Typography className="info-label">{t('totalAmount')}</Typography>
            <Typography className="info-value">BTC 0 (0,00 $)</Typography>
          </div>

          {/* Отображение ошибки баланса */}
          {balanceError && (
            <div className="balance-error">
              <ErrorOutlineIcon style={{ color: 'white', fontSize: 20 }} />
              <Typography className="error-text">
                {balanceError}
              </Typography>
            </div>
          )}

          <Button
            fullWidth
            variant="contained"
            className="action-button"
            disabled={isButtonDisabled}
            onClick={handleOpenConfirmModal}
          >
            {activeTab === 0 ? t('withdrawal') : t('deposit')}
          </Button>
        </div>
      </Paper>

      <DepositConfirmModal
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDeposit}
        data={{
          amount: amount,
          methodName: selectedPaymentMethod,
          fee: "TRX 1.00000000 (0,32 $)", // Эти данные нужно будет расcчитывать
          total: "TRX 99 (31,58 $)", // или получать от API
        }}
      />
    </>
  );
}

function PaymentsHistory() {
  const { t } = useLanguage();
  return (
    <Paper elevation={0} className="card payments-history-card">
      <Typography variant="h6" className="card-title">
        {t('payments')}
      </Typography>
      <Box className="placeholder-content">
        {/* <InboxOutlinedIcon className="placeholder-icon" /> */}
        <Typography variant="h6" className="placeholder-title">
          {t('nothingHere')}
        </Typography>
        <Typography variant="body2" className="card-subtitle">
          {t('noPayments')}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function Payments() {
  return (
    <div className="payments-page-layout">
      <div className="payments-left-column">
        <WithdrawDepositForm />
      </div>
      <div className="payments-right-column">
        <PaymentsHistory />
      </div>
    </div>
  );
}
